import {
	useUpdateProjectMutation,
	useCreateProjectAdditionalContactInformationMutation,
	useUpdateProjectAdditionalContactInformationMutation,
	useCreateProjectAdditionalContactPersonInformationMutation,
	useUpdateProjectAdditionalContactPersonInformationMutation,
	useCreateProjectAdditionalAccountingInformationMutation,
	useUpdateProjectAdditionalAccountingInformationMutation,
	useCreateProjectAdditionalTemplateInformationMutation,
	useUpdateProjectAdditionalTemplateInformationMutation,
} from "@repo/server-api";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useNotifier } from "../../../shared/src/common/useNotifier";

import type { Project } from "../types/ProjectTypes";

type UseProjectOptions = {
	onSaved: (project: Project) => void;
};

type UseProjectResult = {
	updateProject: (project: Project) => void;
	loading: boolean;
};

// Here is the hook
export function useUpdateProjectHook(options: UseProjectOptions): UseProjectResult {
	const { t } = useTranslation();
	const { notifySuccess, notifyException } = useNotifier();

	const { onSaved } = options;
	const [loading, setLoading] = useState(false);

	const [updateProjectMutation] = useUpdateProjectMutation();
	const [createAccounting] = useCreateProjectAdditionalAccountingInformationMutation();
	const [updateAccounting] = useUpdateProjectAdditionalAccountingInformationMutation();
	const [createContact] = useCreateProjectAdditionalContactInformationMutation();
	const [updateContact] = useUpdateProjectAdditionalContactInformationMutation();
	const [createContactPerson] = useCreateProjectAdditionalContactPersonInformationMutation();
	const [updateContactPerson] = useUpdateProjectAdditionalContactPersonInformationMutation();
	const [createTemplateInformation] = useCreateProjectAdditionalTemplateInformationMutation();
	const [updateTemplateInformation] = useUpdateProjectAdditionalTemplateInformationMutation();

	const updateProject = async (project: Project) => {
		setLoading(true);
		try {
			await updateProjectMutation({
				variables: {
					id: project.id,
					name: project.name,
					from: project.startDate!,
					to: project.endDate!,
					budget: {
						amount: project.budgetPrice,
						hasAmountLimit: project.hasBudgetPrice,
						hasTimeLimit: project.hasBudgetHours,
						hours: project.budgetHours,
					},
				},
			});

			// Contact Information
			if (project.contactInformation.id) {
				await updateContact({
					variables: {
						projectId: project.id,
						informationId: project.contactInformation.id,
						clientId: project.contactInformation.client?.id,
						contractorId: project.contactInformation.contractor?.id ?? "",
					},
				});
			} else {
				const createResult = await createContact({
					variables: {
						projectId: project.id,
						clientId: project.contactInformation.client?.id,
						contractorId: project.contactInformation.contractor?.id ?? "",
					},
				});

				const id =
					createResult.data?.createProjectAdditionalContactInformation.additionalContactInformation
						?.id;
				if (id) project.contactInformation.id = id;
			}

			// Contact Person Information
			if (project.contactPersonInformation.id) {
				await updateContactPerson({
					variables: {
						projectId: project.id,
						informationId: project.contactPersonInformation.id,
						clientContactId: project.contactPersonInformation.clientContactPerson?.id,
						contractorContactId: project.contactPersonInformation.contractorContactPerson?.id,
						contractorProjectLeaderId: project.contactPersonInformation.contractorProjectLeader?.id,
					},
				});
			} else {
				const createResult = await createContactPerson({
					variables: {
						projectId: project.id,
						clientContactId: project.contactPersonInformation.clientContactPerson?.id,
						contractorContactId: project.contactPersonInformation.contractorContactPerson?.id,
						contractorProjectLeaderId: project.contactPersonInformation.contractorProjectLeader?.id,
					},
				});

				const id =
					createResult.data?.createProjectAdditionalContactPersonInformation
						.additionalContactPersonInformation?.id;
				if (id) project.contactPersonInformation.id = id;
			}

			// Accounting Information
			if (project.accountingInformation.id) {
				await updateAccounting({
					variables: {
						id: project.id,
						informationId: project.accountingInformation.id,
						accountingProfileId: project.accountingInformation.accountingProfile?.id,
						timeTrackingResolution: project.accountingInformation.timeTrackingResolution ?? 15,
						provision: project.accountingInformation.provision,
						clientContractNumber: project.accountingInformation.clientContractNumber,
						clientContratDate: project.accountingInformation.clientContractDate,
						clientOrderingDate: project.accountingInformation.clientOrderingDate,
						clientOrderingNumber: project.accountingInformation.clientOrderingNumber,
						clientProjectNumber: project.accountingInformation.clientProjectNumber,
						supplierProjectNumber: project.accountingInformation.supplierProjectNumber,
						provisionDescription: project.accountingInformation.provisionDescription,
					},
				});
			} else {
				const createResult = await createAccounting({
					variables: {
						projectId: project.id,
						accountingProfileId: project.accountingInformation.accountingProfile?.id ?? null,
						timeTrackingResolution: project.accountingInformation.timeTrackingResolution ?? 15,
						provision: project.accountingInformation.provision!,
						clientContractNumber: project.accountingInformation.clientContractNumber!,
						clientContratDate: project.accountingInformation.clientContractDate!,
						clientOrderingDate: project.accountingInformation.clientOrderingDate!,
						clientOrderingNumber: project.accountingInformation.clientOrderingNumber!,
						clientProjectNumber: project.accountingInformation.clientProjectNumber!,
						supplierProjectNumber: project.accountingInformation.supplierProjectNumber!,
						provisionDescription: project.accountingInformation.provisionDescription,
					},
				});

				const id =
					createResult.data?.createProjectAdditionalAccountingInformation
						.additionalAccountingInformation?.id;
				if (id) project.accountingInformation.id = id;
			}

			// Template Information
			if (project.TemplateInformation.id) {
				await updateTemplateInformation({
					variables: {
						projectId: project.id,
						informationId: project.TemplateInformation.id,
						isInvoiceWithTimesheet: project.TemplateInformation.isInvoiceWithTimesheet,
						isDetailTimesheet: project.TemplateInformation.isDetailTimesheet,
					},
				});
			} else {
				const createResult = await createTemplateInformation({
					variables: {
						projectId: project.id,
						isInvoiceWithTimesheet: project.TemplateInformation.isInvoiceWithTimesheet,
						isDetailTimesheet: project.TemplateInformation.isDetailTimesheet,
					},
				});

				const id =
					createResult.data?.createProjectAdditionalTemplateInformation
						.additionalTemplateInformation?.id;
				if (id) project.TemplateInformation.id = id;
			}

			notifySuccess(t("project-position-updated"));
			onSaved(project);
		} catch (error) {
			notifyException(error);
		} finally {
			setLoading(false);
		}
	};

	return {
		updateProject,
		loading,
	};
}
